import React, { useState, startTransition, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  HomePage,
  // Individual pages that are currently used
  ServiceDetailsPage,
  NewServicePage,
  MostBookedServicesPage,
  RequestCustomServicePage,
  PoojaDetailsPage,
  PanditProfilePage,
  PanditDashboardPage,
  BookingConfirmationPage,
  BookingSummaryPage,
  WeddingServicesPage,
  HomePoojaServicesPage,
  FestivalServicesPage,
  LifeEventsPage,
  SpiritualServicesPage,
  SpecialOccasionsPage,
  BookingPage,
  AdminDashboardPage
} from './pages';
import SplashScreen from './components/SplashScreen';
import FloatingWhatsAppButton from './components/FloatingWhatsAppButton';
import { RUNTIME_CONFIG } from './config/runtimeConfig';
import { NavigationStateProvider } from './context/navigationContext';
import { initializeLeadDelivery } from './services/leadCaptureService';
import { 
  NavigationWrapper, 
  preloadCriticalPages 
} from './utils/navigationUtils';

// Loading fallback component
const PageLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="loading-spinner w-8 h-8"></div>
    <span className="ml-3 text-gray-600">Loading...</span>
  </div>
);

const DEV_NAV_ITEMS = [
  { key: 'splash', label: 'Splash', tone: 'bg-gray-700' },
  { key: 'home', label: 'Home', tone: 'bg-gray-700' },
  { key: 'wedding-services', label: 'Wedding', tone: 'bg-gray-700' },
  { key: 'home-pooja-services', label: 'Home Pooja', tone: 'bg-gray-700' },
  { key: 'festival-services', label: 'Festivals', tone: 'bg-gray-700' },
  { key: 'life-events', label: 'Life Events', tone: 'bg-gray-700' },
  { key: 'spiritual-services', label: 'Spiritual', tone: 'bg-gray-700' },
  { key: 'special-occasions', label: 'Special', tone: 'bg-gray-700' },
  { key: 'request-custom-service', label: 'Custom', tone: 'bg-gray-700' },
  { key: 'service-details', label: 'Service Details', tone: 'bg-green-700' },
  { key: 'booking', label: 'Booking', tone: 'bg-green-700' },
  { key: 'booking-confirmation', label: 'Booking Success', tone: 'bg-green-700' },
  { key: 'pooja-details', label: 'Pooja Details', tone: 'bg-gray-700' },
  { key: 'pandit-profile', label: 'Pandit Profile', tone: 'bg-gray-700' },
  { key: 'pandit-dashboard', label: 'Pandit Dashboard', tone: 'bg-blue-700' },
  { key: 'booking-summary', label: 'Booking Summary', tone: 'bg-gray-700' },
  { key: 'admin-dashboard', label: 'Admin', tone: 'bg-blue-800' }
];

const QueryPage = ({ serviceName, onCancel, onSubmit }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16">
    <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Ask a Question</h2>
      <p className="text-gray-600 mb-4">About: {serviceName}</p>
      <textarea
        placeholder="Type your question here..."
        className="w-full p-3 border border-gray-300 rounded-lg mb-4"
        rows={4}
      />
      <div className="flex space-x-3">
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300"
        >
          Cancel
        </button>
        <button
          onClick={onSubmit}
          className="flex-1 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700"
        >
          Submit
        </button>
      </div>
    </div>
  </div>
);

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [pageData, setPageData] = useState(null);
  const [navigationStack, setNavigationStack] = useState([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const transitionTimerRef = useRef(null);

  useEffect(() => {
    const currentState = window.history.state;
    if (!currentState || !currentState.__appNav) {
      window.history.replaceState({ __appNav: true, page: 'home', data: null }, '', window.location.href);
    }
  }, []);

  // Preload critical pages on app start
  useEffect(() => {
    preloadCriticalPages();
    initializeLeadDelivery();
  }, []);

  const navigateTo = useCallback((page, data = null, options = {}) => {
    const { fromBack = false, replace = false } = options;

    if (!fromBack && !replace && page !== currentPage) {
      setNavigationStack((prev) => ([...prev, { page: currentPage, data: pageData || null }]));
    }

    if (!fromBack && page !== currentPage) {
      const navState = { __appNav: true, page, data: data || null };
      if (replace) {
        window.history.replaceState(navState, '', window.location.href);
      } else {
        window.history.pushState(navState, '', window.location.href);
      }
    }

    if (transitionTimerRef.current) {
      clearTimeout(transitionTimerRef.current);
    }

    startTransition(() => {
      setIsTransitioning(true);
      setCurrentPage(page);
      setPageData(data || null);
      transitionTimerRef.current = setTimeout(() => setIsTransitioning(false), 100);
    });
  }, [currentPage, pageData]);

  const handleNavigateWithState = useCallback((page, data = null) => {
    navigateTo(page, data);
  }, [navigateTo]);

  const handleGoBack = useCallback(() => {
    if (navigationStack.length === 0) {
      return;
    }

    window.history.back();
  }, [navigationStack, navigateTo]);

  useEffect(() => {
    const handlePopState = (event) => {
      const state = event?.state;

      if (state && state.__appNav) {
        setNavigationStack((prev) => (prev.length > 0 ? prev.slice(0, -1) : prev));
        navigateTo(state.page || 'home', state.data || null, { fromBack: true, replace: true });
        return;
      }

      setNavigationStack([]);
      navigateTo('home', null, { fromBack: true, replace: true });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigateTo]);

  useEffect(() => {
    return () => {
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
      }
    };
  }, []);

  const pageRenderers = useMemo(() => ({
    splash: () => <SplashScreen />,
    home: () => <HomePage onNavigate={handleNavigateWithState} />,
    'wedding-services': () => <WeddingServicesPage onNavigate={handleNavigateWithState} />,
    'home-pooja-services': () => <HomePoojaServicesPage onNavigate={handleNavigateWithState} />,
    'festival-services': () => <FestivalServicesPage onNavigate={handleNavigateWithState} />,
    'life-events': () => <LifeEventsPage onNavigate={handleNavigateWithState} />,
    'spiritual-services': () => <SpiritualServicesPage onNavigate={handleNavigateWithState} />,
    'special-occasions': () => <SpecialOccasionsPage onNavigate={handleNavigateWithState} />,
    'request-custom-service': () => <RequestCustomServicePage onNavigate={handleNavigateWithState} />,
    'most-booked-services': () => <MostBookedServicesPage onNavigate={handleNavigateWithState} />,
    'new-services': () => <NewServicePage onNavigate={handleNavigateWithState} />,
    'pooja-details': () => <PoojaDetailsPage />,
    'pandit-profile': () => (
      <PanditProfilePage
        onNavigate={handleNavigateWithState}
        pandit={pageData?.pandit || null}
      />
    ),
    'pandit-dashboard': () => (
      <PanditDashboardPage
        onNavigate={handleNavigateWithState}
      />
    ),
    'booking-summary': () => <BookingSummaryPage />,
    'booking-confirmation': () => (
      <BookingConfirmationPage
        onNavigate={handleNavigateWithState}
        bookingData={pageData?.bookingData}
      />
    ),
    'service-details': () => (
      <ServiceDetailsPage
        onNavigate={handleNavigateWithState}
        service={pageData?.service}
      />
    ),
    booking: () => (
      <BookingPage
        onNavigate={handleNavigateWithState}
        bookingData={pageData?.bookingData}
        servicePage={pageData?.servicePage || 'wedding-services'}
      />
    ),
    'admin-dashboard': () => (
      <AdminDashboardPage
        onNavigate={handleNavigateWithState}
        initialModuleKey={pageData?.moduleKey}
      />
    ),
    query: () => (
      <QueryPage
        serviceName={pageData?.service?.name}
        onCancel={() => handleNavigateWithState('wedding-services')}
        onSubmit={() => {
          alert('Question submitted!');
          handleNavigateWithState('wedding-services');
        }}
      />
    )
  }), [handleNavigateWithState, pageData]);

  const renderCurrentPage = pageRenderers[currentPage] || pageRenderers.home;

  return (
    <div className="App">
      {/* Demo Navigation Bar - Only for development */}
      {RUNTIME_CONFIG.SHOW_DEV_NAV && (
        <div className="fixed top-0 left-0 right-0 bg-black bg-opacity-75 text-white text-xs p-2 z-50 overflow-x-auto">
          <div className="flex space-x-2 min-w-max">
            {DEV_NAV_ITEMS.map((item) => (
              <button
                key={item.key}
                onClick={() => handleNavigateWithState(item.key)}
                className={`px-2 py-1 ${item.tone} rounded`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Main Content with Suspense for lazy loading */}
      <div className={RUNTIME_CONFIG.SHOW_DEV_NAV ? 'pt-10' : ''}>
        <NavigationStateProvider
          value={{
            canGoBack: navigationStack.length > 0,
            goBack: handleGoBack,
            currentPage
          }}
        >
          <NavigationWrapper currentPage={currentPage}>
            {isTransitioning ? <PageLoadingFallback /> : renderCurrentPage()}
          </NavigationWrapper>
        </NavigationStateProvider>
      </div>

      {/* Floating WhatsApp Button */}
      <FloatingWhatsAppButton />
    </div>
  );
}

export default App;
